/**
 * Admin Field-Agent Productivity Detail — Release 3 (2026-04-23)
 * /admin/field-agents/:id/productivity
 *
 * Sections (per contract spec):
 *   - Summary cards
 *   - 90-day Contract progress panel
 *   - KPI score breakdown
 *   - Weekly trend
 *   - Pipeline summary
 *   - Activity / consistency panel
 *   - Recent leads
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  ArrowLeft, Loader2, Target, TrendingUp, Users, Package, Store,
  CheckCircle2, Clock, AlertTriangle, Activity, Gauge, Calendar,
} from "lucide-react";

const bandColor = (band) => ({
  excellent: "bg-emerald-500/15 text-emerald-700",
  strong: "bg-sky-500/15 text-sky-700",
  fair: "bg-amber-500/15 text-amber-700",
  needs_attention: "bg-rose-500/15 text-rose-700",
})[band] || "bg-slate-500/15 text-slate-700";

const paceColor = (label) => ({
  ahead: "bg-emerald-500/15 text-emerald-700",
  on_pace: "bg-sky-500/15 text-sky-700",
  behind: "bg-rose-500/15 text-rose-700",
  no_contract: "bg-slate-500/15 text-slate-500",
})[label] || "bg-slate-500/15";

function ProgressBar({ pct, color = "bg-emerald-500" }) {
  const w = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${w}%` }} />
    </div>
  );
}

export default function AdminFieldAgentProductivity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/field-agents/${id}/productivity`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load productivity");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  const { agent, contract, kpi, pipeline, weekly_trend, recent_leads, activity_14d } = data;
  const weeklyMax = Math.max(1, ...(weekly_trend || []).map((w) => Math.max(w.submissions, w.approvals)));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-field-agent-productivity-page">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/field-agents")} data-testid="back-to-agents">
            <ArrowLeft className="w-4 h-4 mr-1" /> Agents
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900">{agent.name}</h1>
            <p className="text-slate-500 text-sm font-mono">{agent.id} · {agent.phone}</p>
          </div>
          <Badge className={paceColor(contract.status_label)} data-testid="pace-badge">
            {contract.status_label.replace("_", " ").toUpperCase()}
          </Badge>
          <Badge className={bandColor(kpi.band)} data-testid="band-badge">
            KPI {kpi.weighted_total} · {kpi.band.replace("_", " ")}
          </Badge>
        </div>

        {/* Summary cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <SummaryCard icon={<Store className="w-5 h-5 text-emerald-600" />} label="PUDO approved" value={pipeline.pudo.approved} sub={`${pipeline.pudo.total} total`} testid="card-pudo" />
          <SummaryCard icon={<Users className="w-5 h-5 text-sky-600" />} label="Drivers approved" value={pipeline.driver.approved} sub={`${pipeline.driver.total} total`} testid="card-driver" />
          <SummaryCard icon={<Package className="w-5 h-5 text-violet-600" />} label="ClockInJA leads" value={pipeline.clockinja.approved} sub={`${pipeline.clockinja.total} total`} testid="card-clockinja" />
          <SummaryCard icon={<Activity className="w-5 h-5 text-amber-600" />} label="Last activity" value={agent.last_login_at ? agent.last_login_at.slice(0, 10) : "—"} sub={activity_14d.active_days + "/" + activity_14d.window_days + " days active (14d)"} testid="card-last" />
        </div>

        {/* Contract progress */}
        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="contract-panel">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-emerald-600" />
            <h2 className="text-xl font-bold">90-day Contract progress</h2>
            {contract.contract_value && (
              <Badge className="ml-auto bg-slate-100 text-slate-700">Contract value: JMD ${contract.contract_value.toLocaleString()}</Badge>
            )}
          </div>
          {!contract.contract_start_date ? (
            <div className="text-sm text-slate-400 italic">Contract not set. Edit the agent to set start/end dates and targets.</div>
          ) : (
            <>
              <div className="grid md:grid-cols-4 gap-4 mb-4 text-sm">
                <div><div className="text-xs text-slate-400">Start</div><div className="font-semibold">{contract.contract_start_date}</div></div>
                <div><div className="text-xs text-slate-400">End</div><div className="font-semibold">{contract.contract_end_date}</div></div>
                <div><div className="text-xs text-slate-400">Days elapsed / remaining</div><div className="font-semibold">{contract.days_elapsed} / {contract.days_remaining}</div></div>
                <div><div className="text-xs text-slate-400">Expected progress</div><div className="font-semibold">{contract.expected_progress_pct}%</div></div>
              </div>
              <div className="space-y-3">
                <ContractRow label="Overall" pct={contract.progress_pct.overall} expected={contract.expected_progress_pct} />
                <ContractRow label="PUDO centres" pct={contract.progress_pct.pudo} actual={contract.actuals.pudo_approved} target={contract.targets.pudo} />
                <ContractRow label="Drivers" pct={contract.progress_pct.drivers} actual={contract.actuals.drivers_approved} target={contract.targets.drivers} />
                <ContractRow label="ClockInJA" pct={contract.progress_pct.clockinja} actual={contract.actuals.clockinja_approved} target={contract.targets.clockinja} />
              </div>
            </>
          )}
        </section>

        {/* KPI score breakdown */}
        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="kpi-panel">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-bold">KPI score breakdown</h2>
            <Badge className={`ml-auto ${bandColor(kpi.band)} text-base px-3 py-1`}>{kpi.weighted_total} / 100</Badge>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <KpiCard label="Activity" weight="25%" score={kpi.activity_score} testid="kpi-activity" />
            <KpiCard label="Conversions" weight="35%" score={kpi.conversion_score} testid="kpi-conv" />
            <KpiCard label="Verification" weight="20%" score={kpi.verification_score} testid="kpi-verif" />
            <KpiCard label="Live output" weight="20%" score={kpi.live_output_score} testid="kpi-live" />
          </div>
        </section>

        {/* Weekly trend */}
        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="weekly-panel">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-sky-600" />
            <h2 className="text-xl font-bold">Weekly trend</h2>
            <span className="ml-auto text-xs text-slate-400">{weekly_trend.length} weeks</span>
          </div>
          <div className="grid grid-cols-6 md:grid-cols-13 gap-2 mb-4" style={{ gridTemplateColumns: `repeat(${weekly_trend.length}, minmax(0,1fr))` }}>
            {weekly_trend.map((w, i) => (
              <div key={i} className="flex flex-col items-center text-[10px] text-slate-500" data-testid={`week-${i}`}>
                <div className="h-24 w-full flex flex-col justify-end gap-0.5 mb-1">
                  <div className="bg-sky-400 rounded" style={{ height: `${(w.submissions / weeklyMax) * 100}%`, minHeight: w.submissions ? "3px" : "0" }} title={`${w.submissions} submissions`} />
                  <div className="bg-emerald-500 rounded" style={{ height: `${(w.approvals / weeklyMax) * 100}%`, minHeight: w.approvals ? "3px" : "0" }} title={`${w.approvals} approvals`} />
                </div>
                <div>{w.week_start.slice(5)}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-sky-400 inline-block rounded" /> Submissions</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 inline-block rounded" /> Approvals</span>
          </div>
          {/* Weekly review table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="weekly-review-table">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr><th className="text-left px-3 py-2">Week</th><th className="text-right px-3 py-2">Submissions</th><th className="text-right px-3 py-2">Approvals</th><th className="text-right px-3 py-2">Rate</th></tr>
              </thead>
              <tbody>
                {weekly_trend.slice(-8).reverse().map((w, i) => {
                  const rate = w.submissions ? Math.round((w.approvals / w.submissions) * 100) : null;
                  return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2">{w.week_start} → {w.week_end}</td>
                      <td className="px-3 py-2 text-right">{w.submissions}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{w.approvals}</td>
                      <td className="px-3 py-2 text-right">{rate !== null ? `${rate}%` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pipeline summary + Activity/consistency */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="pipeline-panel">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Store className="w-5 h-5 text-emerald-600" /> Pipeline summary</h2>
            <div className="space-y-3 text-sm">
              {["pudo", "driver", "clockinja"].map((k) => (
                <PipelineRow key={k} label={k.toUpperCase()} row={pipeline[k]} />
              ))}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="consistency-panel">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-600" /> Activity / consistency</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-slate-900">{activity_14d.active_days}</div>
                <div className="text-xs text-slate-500">Active days (14d)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-900">{activity_14d.consistency_pct}%</div>
                <div className="text-xs text-slate-500">Consistency</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-900">{activity_14d.window_days - activity_14d.active_days}</div>
                <div className="text-xs text-slate-500">Silent days</div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Based on any submission across PUDO, driver, or ClockInJA pipelines in the last 14 days (JM-local day partition by `created_at` prefix).
              Phase 2 will add weekly reporting compliance + evidence scoring.
            </p>
          </section>
        </div>

        {/* Recent leads */}
        <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="recent-leads-panel">
          <h2 className="text-xl font-bold mb-4">Recent leads</h2>
          {recent_leads.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No leads yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Added</th></tr>
              </thead>
              <tbody>
                {recent_leads.map((l) => (
                  <tr key={l.type + l.id} className="border-t border-slate-100">
                    <td className="px-3 py-2"><Badge className="bg-slate-100 text-slate-700 text-xs">{l.type}</Badge></td>
                    <td className="px-3 py-2 font-medium">{l.name || "(unnamed)"}</td>
                    <td className="px-3 py-2">
                      {l.approval_status === "approved" ? <Badge className="bg-emerald-500/15 text-emerald-700 text-xs">approved</Badge>
                        : l.approval_status === "rejected" ? <Badge className="bg-rose-500/15 text-rose-700 text-xs">rejected</Badge>
                        : <Badge className="bg-amber-500/15 text-amber-700 text-xs">{l.approval_status || "pending"}</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span></div>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}

function ContractRow({ label, pct, expected, actual, target }) {
  const p = pct ?? 0;
  const color = p >= (expected ?? 50) ? "bg-emerald-500" : "bg-amber-400";
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">
          {actual !== undefined && target !== undefined ? `${actual} / ${target}` : ""}  <span className="ml-2 font-semibold">{p}%</span>
        </span>
      </div>
      <ProgressBar pct={p} color={color} />
    </div>
  );
}

function KpiCard({ label, weight, score, testid }) {
  const color = score >= 75 ? "bg-emerald-500" : score >= 60 ? "bg-sky-500" : score >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="bg-slate-50 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-slate-700">{label}</span><span className="text-xs text-slate-500">{weight}</span></div>
      <div className="text-2xl font-bold text-slate-900 mb-2">{score}</div>
      <ProgressBar pct={score} color={color} />
    </div>
  );
}

function PipelineRow({ label, row }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-xs text-slate-500">{row.total} total</span>
      </div>
      <div className="flex gap-2 text-xs">
        <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-700 px-2 py-0.5 rounded"><CheckCircle2 className="w-3 h-3" /> {row.approved} approved</span>
        <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-700 px-2 py-0.5 rounded"><Clock className="w-3 h-3" /> {row.pending} pending</span>
        {row.rejected !== undefined && <span className="inline-flex items-center gap-1 bg-rose-500/15 text-rose-700 px-2 py-0.5 rounded"><AlertTriangle className="w-3 h-3" /> {row.rejected} rejected</span>}
      </div>
    </div>
  );
}
